// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { RadioPresentationProps } from "@insite/mobius/Radio";
import { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import { CustomAppState } from "@sc/App/Store/Reducers";
import flatMap from "lodash/flatMap";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const {
        accountSettings: { enableWarehousePickup, allowEmptyShipping },
        cartSettings: { canRequestDeliveryDate, enableRequestPickUpDate, maximumDeliveryPeriod },
    } = getSettingsCollection(state);
    const session = getSession(state);
    const { pickUpWarehouse } = session;
    const { cartId, isPlacingOrder } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart, isLoading: isCartLoading } = cartState;
    // XNG-Change
    const fulfillmentMethod = cart?.isAwaitingApproval ? cart.fulfillmentMethod : session.fulfillmentMethod;
    return {
        cart,
        isCartLoading,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showRequestPickUpDate:
            enableWarehousePickup && enableRequestPickUpDate && fulfillmentMethod === FulfillmentMethod.PickUp,
        showRequestDeliveryDate: canRequestDeliveryDate,
        maximumDeliveryPeriod,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        pickUpWarehouse,
        allowEmptyShipping,
        // XNG-Change
        fulfillmentMethod,
        shipWhenCompleteFromStore: state.xCheckout.shipWhenComplete,
        isPlacingOrder,
        requestedPickupDate: state.xCheckout.requestedPickupDate,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutDeliveryMethodStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    editGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    fulfillmentMethodGridItem?: GridItemProps;
    fulfillmentMethodText?: TypographyPresentationProps;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    servicesGridItem?: GridItemProps;
    servicesRadioGroup?: RadioGroupPresentationProps;
    serviceRadio?: RadioPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    // XNG-Change
    wrapper?: InjectableCss;
    shipWhenCompleteCheckbox?: CheckboxGroupProps;
}

export const onePageCheckoutDeliveryMethodStyles: OnePageCheckoutDeliveryMethodStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    titleGridItem: {
        width: 10,
        css: css`
            align-items: center;
        `,
    },
    titleText: {
        variant: "h5",
        transform: "capitalize",
        css: css`
            margin-bottom: 0;
        `,
    },
    editGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    fulfillmentMethodGridItem: { width: 12 },
    fulfillmentMethodText: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    servicesGridItem: { width: 12 },
    deliveryDateGridItem: { width: 12 },
    pickUpDateGridItem: { width: 12 },
};

const styles = onePageCheckoutDeliveryMethodStyles;

const OnePageCheckoutDeliveryMethod = ({
    cart,
    isCartLoading,
    cartUrl,
    pickUpWarehouse,
    showShippingMethod,
    showRequestPickUpDate,
    showRequestDeliveryDate,
    maximumDeliveryPeriod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setShippingMethod,
    allowEmptyShipping,
    // XNG-Change
    fulfillmentMethod,
    shipWhenCompleteFromStore,
    isPlacingOrder,
    requestedPickupDate,
}: Props) => {
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [isUpdatingCart, setIsUpdatingCart] = useState(false);

    // XNG-Change: add ship when complete functions
    const [shipWhenComplete, setShipWhenComplete] = useState(false);
    const [showFormErrors, setShowFormErrors] = useState(false);
    // XNG-Change
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (isPlacingOrder) {
            setShowFormErrors(true);
        }
    }, [isPlacingOrder]);

    const shipWhenCompleteChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setShipWhenComplete(value);
        dispatch({ type: "xCheckout/SetShipWhenComplete", shipWhenComplete: value });
    };

    useEffect(() => {
        if (cart) {
            setSelectedShipVia(cart.shipVia);
            setIsUpdatingCart(false);
        }
    }, [cart?.carrier, cart?.shipVia]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="onePageCheckout_deliveryMethodLoading" />
            </StyledWrapper>
        );
    }
    // XNG-Change
    const serviceChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!cart.carriers) {
            return;
        }

        const shipVias = flatMap(cart.carriers, o => o.shipVias).filter(o => !!o) as ShipViaDto[];
        const shipVia = shipVias.find(o => o.id === event.currentTarget.value);
        if (!shipVia) {
            return;
        }

        const carrier = cart.carriers.find(o => o.shipVias?.some(p => p.id === shipVia.id));
        if (!carrier) {
            return;
        }

        setIsUpdatingCart(true);
        setSelectedShipVia(shipVia);
        setShippingMethod({ carrier, shipVia });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
        dispatch({ type: "xCheckout/SetRequestedPickupDate", requestedPickupDate: selectedDay });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleGridItem}>
                <Typography as="h2" {...styles.titleText}>
                    {/* XNG-Change */}
                    {fulfillmentMethod === FulfillmentMethod.PickUp ? translate("Pick Up") : translate("Shipping")}
                </Typography>
            </StyledWrapper>
            {/* <StyledWrapper {...styles.editGridItem}>
                <Link {...styles.editLink} href={cartUrl} data-test-selector="deliveryMethod_editButton">
                    {translate("Edit")}
                </Link>
            </StyledWrapper> 
            <StyledWrapper {...styles.fulfillmentMethodGridItem}>
                <Typography
                    as="p"
                    {...styles.fulfillmentMethodText}
                    data-test-selector="onePageCheckout_fulfillmentMethod"
                >
                    {showShippingMethod
                        ? translate("Ship to home")
                        : `${translate("Store pickup at")} ${pickUpWarehouse?.description || pickUpWarehouse?.name}`}
                </Typography> 
            </StyledWrapper> */}
            {showShippingMethod && (
                <>
                    {cart.carriers?.length === 0 && !allowEmptyShipping && (
                        <StyledWrapper {...styles.noCarriersFoundGridItem}>
                            <Notify type="danger" {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Notify>
                        </StyledWrapper>
                    )}
                    {cart.carriers.length > 0 && (
                        <StyledWrapper {...styles.servicesGridItem}>
                            {/* XNG-Change replace dropdown with site message */}
                            {/* <Select
                                label={translate("Shipping Method")}
                                value={selectedShipVia?.id}
                                disabled={isCartLoading || isUpdatingCart}
                                onChange={serviceChangeHandler}
                                data-test-selector="onePageCheckout_chooseDeliveryMethod"
                                required={true}
                                {...{ displayStar: true }}
                            >
                                {flatMap(cart.carriers, carrier =>
                                    carrier.shipVias?.map(shipVia => ({ ...shipVia, carrier })),
                                ).map(shipVia => (
                                    <option
                                        key={shipVia?.id}
                                        value={shipVia?.id}
                                        data-test-selector={`${shipVia?.carrier.description} - ${shipVia?.description}`}
                                    >
                                        {shipVia?.description}
                                    </option>
                                ))}
                            </Select> */}
                            <Typography as="p">{siteMessage("Checkout_ShippingMethodDescription")}</Typography>
                        </StyledWrapper>
                    )}

                    <StyledWrapper
                        {...styles.deliveryDateGridItem}
                        data-test-selector="onePageCheckout_requestedDeliveryDate"
                    >
                        {showRequestDeliveryDate && (
                            <DatePicker
                                {...styles.deliveryDatePicker}
                                label={`${translate("Requested Delivery Date")}`}
                                hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                selectedDay={cart.requestedDeliveryDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), maximumDeliveryPeriod),
                                    monthAriaLabel: translate("Request delivery date month"),
                                    dayAriaLabel: translate("Request delivery date day"),
                                    yearAriaLabel: translate("Request delivery date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                                required={false}
                            />
                        )}
                        {/* XNG-Change: add ship when complete */}
                        <CheckboxGroup {...styles.shipWhenCompleteCheckbox}>
                            <Checkbox checked={shipWhenComplete} onChange={shipWhenCompleteChangeHandler}>
                                {translate("Ship When Complete")}
                            </Checkbox>
                        </CheckboxGroup>
                    </StyledWrapper>
                </>
            )}
            {showRequestPickUpDate && (
                <StyledWrapper {...styles.pickUpDateGridItem} data-test-selector="onePageCheckout_requestPickUpDate">
                    <DatePicker
                        {...styles.pickUpDatePicker}
                        label={`${translate("Request Pick Up Date")}`}
                        hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                        selectedDay={cart.requestedPickupDateDisplay!}
                        dateTimePickerProps={{
                            minDate: new Date(),
                            maxDate: addDays(new Date(), maximumDeliveryPeriod),
                            ...styles.pickUpDatePicker?.dateTimePickerProps,
                        }}
                        onDayChange={handleRequestPickUpDateChanged}
                        monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter month, format M M",
                        )}`}
                        dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter day, format D D",
                        )}`}
                        yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                            "Enter year, format Y Y Y Y",
                        )}`}
                        // XNG-Change
                        required={true}
                        {...{ displayStar: true }}
                        error={showFormErrors && !requestedPickupDate && `${translate("Please choose a valid date")}`}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutDeliveryMethod),
    definition: {
        displayName: "Delivery Method",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
